import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../../components/feature/LanguageSwitcher';

export default function CashlessPage() {
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'B2B特化型キャッシュレス決済導入支援 | 株式会社LIKES - 福岡市 DX推進 GMO提携';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '株式会社LIKESのB2B特化型キャッシュレス決済導入支援。福岡市の企業間取引に最適化された決済システムで業務効率化とキャッシュフロー改善を実現。GMO・ライフカードと提携し安心のサポート体制。');
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/services/cashless`);
    }
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const schemas = [
      { id: 'schema-service-ca', data: { '@context': 'https://schema.org', '@type': 'Service', name: 'B2B特化型キャッシュレス決済導入支援', provider: { '@type': 'Organization', '@id': `${siteUrl}/#organization` } } },
      { id: 'schema-breadcrumb-ca', data: { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: [{ '@type': 'ListItem', position: 1, name: 'ホーム', item: `${siteUrl}/` }, { '@type': 'ListItem', position: 2, name: 'B2B特化型キャッシュレス決済導入支援', item: `${siteUrl}/services/cashless` }] } },
    ];
    schemas.forEach(({ id, data }) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(data);
      script.id = id;
      document.head.appendChild(script);
    });
    return () => { schemas.forEach(({ id }) => { document.getElementById(id)?.remove(); }); };
  }, []);

  const serviceCards = [
    {
      icon: 'ri-line-chart-line',
      titleKey: 'page.cashless.merit.title',
      items: ['page.cashless.merit.1','page.cashless.merit.2','page.cashless.merit.3','page.cashless.merit.4'],
    },
    {
      icon: 'ri-bank-card-line',
      titleKey: 'page.cashless.systems.title',
      items: ['page.cashless.systems.1','page.cashless.systems.2','page.cashless.systems.3','page.cashless.systems.4'],
    },
    {
      icon: 'ri-customer-service-line',
      titleKey: 'page.cashless.support.title',
      items: ['page.cashless.support.1','page.cashless.support.2','page.cashless.support.3','page.cashless.support.4'],
    },
  ];

  const flowSteps = [
    { num: '1', titleKey: 'page.cashless.flow.step1.title', descKey: 'page.cashless.flow.step1.desc' },
    { num: '2', titleKey: 'page.cashless.flow.step2.title', descKey: 'page.cashless.flow.step2.desc' },
    { num: '3', titleKey: 'page.cashless.flow.step3.title', descKey: 'page.cashless.flow.step3.desc' },
    { num: '4', titleKey: 'page.cashless.flow.step4.title', descKey: 'page.cashless.flow.step4.desc' },
  ];

  const results = [
    { img: 'https://public.readdy.ai/ai/img_res/e7e8138fdc26310c9f0824db604f0831.jpg', icon: 'ri-time-line', titleKey: 'page.cashless.result1.title', bodyKey: 'page.cashless.result1.body', badgeKey: 'page.cashless.result1.badge' },
    { img: 'https://readdy.ai/api/search-image?query=Strong%20business%20foundation%20with%20improved%20cash%20flow%20management%20through%20digital%20payment%20system%2C%20executive%20team%20analyzing%20financial%20growth%20charts%20and%20positive%20cash%20flow%20indicators%20in%20bright%20modern%20office%2C%20professional%20corporate%20success%20atmosphere&width=600&height=400&seq=cashless-foundation-001&orientation=landscape', icon: 'ri-funds-line', titleKey: 'page.cashless.result2.title', bodyKey: 'page.cashless.result2.body', badgeKey: 'page.cashless.result2.badge' },
    { img: 'https://readdy.ai/api/search-image?query=Digital%20transformation%20success%20with%20modern%20cashless%20payment%20technology%20integration%2C%20business%20team%20using%20advanced%20digital%20systems%20and%20cloud-based%20payment%20platforms%20in%20contemporary%20workspace%2C%20innovation%20and%20technology%20adoption%20concept%20with%20bright%20professional%20atmosphere&width=600&height=400&seq=cashless-digital-001&orientation=landscape', icon: 'ri-rocket-line', titleKey: 'page.cashless.result3.title', bodyKey: 'page.cashless.result3.body', badgeKey: 'page.cashless.result3.badge' },
  ];

  const partners = [
    { icon: 'ri-bank-card-2-line', typeKey: 'page.cashless.partner1.type', nameKey: 'page.cashless.partner1.name', descKey: 'page.cashless.partner1.desc', wide: false },
    { icon: 'ri-secure-payment-line', typeKey: 'page.cashless.partner2.type', nameKey: 'page.cashless.partner2.name', descKey: 'page.cashless.partner2.desc', wide: false },
    { icon: 'ri-exchange-funds-line', typeKey: 'page.cashless.partner3.type', nameKey: 'page.cashless.partner3.name', descKey: 'page.cashless.partner3.desc', wide: true },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex flex-col leading-none cursor-pointer">
              <span style={{ fontFamily: "'Georgia', 'Times New Roman', serif", letterSpacing: '0.18em' }} className="text-[#255485] font-bold text-2xl tracking-widest uppercase">LIKES</span>
              <span style={{ fontFamily: "'Georgia', 'Times New Roman', serif", letterSpacing: '0.22em' }} className="text-[#255485]/70 text-[9px] font-medium tracking-[0.22em] uppercase mt-0.5">Co, Ltd</span>
            </Link>
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/#vision" className="text-sm text-gray-700 hover:text-[#255485] transition-colors duration-200 cursor-pointer">{t('nav.vision')}</Link>
              <Link to="/#services" className="text-sm text-gray-700 hover:text-[#255485] transition-colors duration-200 cursor-pointer">{t('nav.services')}</Link>
              <Link to="/case-study" className="text-sm text-gray-700 hover:text-[#255485] transition-colors duration-200 cursor-pointer">{t('nav.caseStudy')}</Link>
              <Link to="/#csr" className="text-sm text-gray-700 hover:text-[#255485] transition-colors duration-200 cursor-pointer">{t('nav.csr')}</Link>
              <Link to="/#company" className="text-sm text-gray-700 hover:text-[#255485] transition-colors duration-200 cursor-pointer">{t('nav.company')}</Link>
              <LanguageSwitcher />
              <Link to="/#contact" className="text-sm text-gray-700 hover:text-[#255485] transition-colors duration-200 cursor-pointer">{t('nav.contact')}</Link>
            </nav>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden w-10 h-10 flex items-center justify-center text-gray-700 cursor-pointer">
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-gray-200 mt-4 pt-4">
              <nav className="flex flex-col space-y-3">
                <div className="flex items-center gap-2 py-1"><span className="text-xs text-gray-500">Language</span><LanguageSwitcher /></div>
                <Link to="/#vision" className="text-sm text-gray-700 hover:text-[#255485] cursor-pointer py-2">{t('nav.vision')}</Link>
                <Link to="/#services" className="text-sm text-gray-700 hover:text-[#255485] cursor-pointer py-2">{t('nav.services')}</Link>
                <Link to="/case-study" className="text-sm text-gray-700 hover:text-[#255485] cursor-pointer py-2">{t('nav.caseStudy')}</Link>
                <Link to="/#csr" className="text-sm text-gray-700 hover:text-[#255485] cursor-pointer py-2">{t('nav.csr')}</Link>
                <Link to="/#company" className="text-sm text-gray-700 hover:text-[#255485] cursor-pointer py-2">{t('nav.company')}</Link>
                <Link to="/#contact" className="text-sm text-gray-700 hover:text-[#255485] cursor-pointer py-2">{t('nav.contact')}</Link>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block bg-[#255485]/10 px-4 py-2 rounded-full mb-6">
                <span className="text-[#255485] text-sm font-medium">{t('page.cashless.hero.badge')}</span>
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-serif text-gray-900 mb-6 leading-tight" style={{ whiteSpace: 'pre-line' }}>{t('page.cashless.hero.title')}</h1>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-8">{t('page.cashless.hero.body')}</p>
              <a href="#contact-cta" className="inline-flex items-center gap-2 bg-[#255485] text-white px-8 py-4 rounded-lg hover:bg-[#1d4166] transition-colors duration-200 cursor-pointer whitespace-nowrap">
                {t('page.cashless.hero.cta')}<i className="ri-arrow-right-line"></i>
              </a>
            </div>
            <div className="w-full h-[400px] md:h-[500px] rounded-2xl overflow-hidden">
              <img src="https://readdy.ai/api/search-image?query=Modern%20B2B%20cashless%20payment%20system%20with%20business%20professionals%20using%20digital%20payment%20terminals%20and%20smartphones%20for%20corporate%20transactions%20in%20contemporary%20office%20environment%2C%20contactless%20payment%20technology%2C%20financial%20innovation%20and%20digital%20transformation%20concept%20with%20clean%20professional%20atmosphere%20and%20bright%20lighting&width=800&height=1000&seq=cashless-hero-001&orientation=portrait" alt="B2B特化型キャッシュレス決済導入支援" className="w-full h-full object-cover object-top" />
            </div>
          </div>
        </div>
      </section>

      {/* Service Details */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-serif text-gray-900 mb-4">{t('page.cashless.services.title')}</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">{t('page.cashless.services.subtitle')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {serviceCards.map((card) => (
              <div key={card.titleKey} className="bg-gray-50 rounded-xl p-8 hover:shadow-lg transition-shadow duration-300">
                <div className="w-16 h-16 flex items-center justify-center bg-[#255485]/10 rounded-xl mb-6">
                  <i className={`${card.icon} text-3xl text-[#255485]`}></i>
                </div>
                <h3 className="text-2xl font-serif text-gray-900 mb-4">{t(card.titleKey)}</h3>
                <ul className="space-y-3 text-gray-600">
                  {card.items.map((itemKey) => (
                    <li key={itemKey} className="flex items-start gap-3">
                      <i className="ri-check-line text-[#255485] mt-1"></i>
                      <span>{t(itemKey)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Flow */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-serif text-gray-900 mb-4">{t('page.cashless.flow.title')}</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">{t('page.cashless.flow.subtitle')}</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
            {flowSteps.map((step, idx) => (
              <div key={step.num} className="relative">
                <div className="bg-white rounded-xl p-8">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#255485] text-white rounded-full text-xl font-bold mb-6">{step.num}</div>
                  <h3 className="text-xl font-serif text-gray-900 mb-3">{t(step.titleKey)}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{t(step.descKey)}</p>
                </div>
                {idx < 3 && (
                  <div className="absolute top-1/2 -right-3 transform -translate-y-1/2 text-2xl text-[#255485] w-6 h-6 flex items-center justify-center">
                    <i className="ri-arrow-right-line"></i>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-serif text-gray-900 mb-4">{t('page.cashless.results.title')}</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">{t('page.cashless.results.subtitle')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {results.map((r) => (
              <div key={r.titleKey} className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-8 border border-gray-100">
                <div className="w-full h-48 rounded-lg overflow-hidden mb-6">
                  <img src={r.img} alt={t(r.titleKey)} className="w-full h-full object-cover object-top" />
                </div>
                <h3 className="text-2xl font-serif text-gray-900 mb-4">{t(r.titleKey)}</h3>
                <p className="text-gray-600 leading-relaxed mb-4">{t(r.bodyKey)}</p>
                <div className="flex items-center gap-2 text-[#255485] font-medium">
                  <i className={r.icon}></i>
                  <span>{t(r.badgeKey)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-serif text-gray-900 mb-4">{t('page.cashless.partners.title')}</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">{t('page.cashless.partners.subtitle')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {partners.filter((p) => !p.wide).map((p) => (
              <div key={p.nameKey} className="bg-white rounded-xl p-8 border border-gray-100">
                <div className="flex items-center gap-4 mb-5">
                  <div className="w-14 h-14 flex items-center justify-center bg-[#255485]/10 rounded-xl">
                    <i className={`${p.icon} text-3xl text-[#255485]`}></i>
                  </div>
                  <div>
                    <p className="text-xs text-[#255485] font-medium tracking-wider uppercase mb-1">{t(p.typeKey)}</p>
                    <h3 className="text-xl font-serif text-gray-900">{t(p.nameKey)}</h3>
                  </div>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">{t(p.descKey)}</p>
              </div>
            ))}
            {partners.filter((p) => p.wide).map((p) => (
              <div key={p.nameKey} className="bg-white rounded-xl p-8 border border-gray-100 col-span-2 max-w-xl mx-auto w-full">
                <div className="flex items-center gap-4 mb-5">
                  <div className="w-14 h-14 flex items-center justify-center bg-[#255485]/10 rounded-xl">
                    <i className={`${p.icon} text-3xl text-[#255485]`}></i>
                  </div>
                  <div>
                    <p className="text-xs text-[#255485] font-medium tracking-wider uppercase mb-1">{t(p.typeKey)}</p>
                    <h3 className="text-xl font-serif text-gray-900">{t(p.nameKey)}</h3>
                  </div>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">{t(p.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="contact-cta" className="py-20 bg-gradient-to-br from-[#255485] to-[#1d4166]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-serif text-white mb-6" style={{ whiteSpace: 'pre-line' }}>{t('page.cashless.cta.title')}</h2>
          <p className="text-white/90 text-base md:text-lg mb-8 leading-relaxed" style={{ whiteSpace: 'pre-line' }}>{t('page.cashless.cta.body')}</p>
          <Link to="/#contact" className="inline-flex items-center gap-2 bg-white text-[#255485] px-10 py-4 rounded-lg hover:bg-gray-100 transition-colors duration-200 font-medium text-lg cursor-pointer whitespace-nowrap">
            {t('page.cashless.cta.button')}<i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#255485] text-white py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-8">
            <div>
              <div className="flex flex-col leading-none mb-4">
                <span style={{ fontFamily: "'Georgia', 'Times New Roman', serif", letterSpacing: '0.18em' }} className="text-white font-bold text-2xl tracking-widest uppercase">LIKES</span>
                <span style={{ fontFamily: "'Georgia', 'Times New Roman', serif", letterSpacing: '0.22em' }} className="text-white/70 text-[9px] font-medium tracking-[0.22em] uppercase mt-0.5">Co, Ltd</span>
              </div>
              <p className="text-white/80 text-sm leading-relaxed" style={{ whiteSpace: 'pre-line' }}>{t('footer.tagline')}</p>
            </div>
            <div>
              <h3 className="font-medium mb-4">{t('subpage.services.label')}</h3>
              <ul className="space-y-2 text-sm text-white/80">
                <li><Link to="/services/consulting" className="hover:text-white transition-colors duration-200 cursor-pointer">{t('nav.consulting')}</Link></li>
                <li><Link to="/services/cashless" className="hover:text-white transition-colors duration-200 cursor-pointer">{t('nav.cashless')}</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-medium mb-4">{t('subpage.company.label')}</h3>
              <ul className="space-y-2 text-sm text-white/80">
                <li><Link to="/#vision" className="hover:text-white transition-colors duration-200 cursor-pointer">{t('nav.vision')}</Link></li>
                <li><Link to="/#csr" className="hover:text-white transition-colors duration-200 cursor-pointer">{t('nav.csr')}</Link></li>
                <li><Link to="/#company" className="hover:text-white transition-colors duration-200 cursor-pointer">{t('nav.company')}</Link></li>
                <li><Link to="/#contact" className="hover:text-white transition-colors duration-200 cursor-pointer">{t('nav.contact')}</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/20 pt-8 flex items-center justify-between">
            <p className="text-white/60 text-sm">{t('subpage.footer.copyright')}</p>
            <a href="https://www.ikimonogakari.jp/" target="_blank" rel="noopener noreferrer" className="cursor-pointer">
              <img src="https://static.readdy.ai/image/26970d9518e2e09002ea7ff933ab8760/5b8b09492cb52be1efa246b8b18519da.png" alt="いきものがかり" className="h-8 brightness-0 invert" />
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
